import React, { useState, useCallback, useEffect } from 'react';
import { Skeleton, Empty, Divider } from 'antd';
import { GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../../redux/store';
import { fetchDomains, addDomain, removeDomain } from '../store/settingsSlice';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  SaveChangesButton,
  DomainSkeletonList,
  EmptyDescription,
} from '../Styles';
import { CARD_ICON_STYLE } from '../constants';
import { notification } from '../../../commonComponents/Notification/Notification';
import type { DomainSectionProps } from '../types';
import type { PlatformKey } from '../constants';
import DomainCard from './DomainCard';
import AddDomainForm from './AddDomainForm';

const DomainSection: React.FC<DomainSectionProps> = ({ profileLoading }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { domains, loading } = useSelector((state: RootState) => state.settings);

  const [showAddForm, setShowAddForm] = useState(false);
  const [removingId, setRemovingId] = useState<string | number | null>(null);

  useEffect(() => {
    dispatch(fetchDomains());
  }, [dispatch]);

  const handleAdd = useCallback(
    async (data: { domain: string; platform: PlatformKey; recordType: 'CNAME' | 'A'; dnsTarget: string }) => {
      try {
        await dispatch(addDomain(data)).unwrap();
        notification.success('Domain Added', `${data.domain} has been added. Configure your DNS to activate it.`);
        setShowAddForm(false);
      } catch (error: any) {
        const message = typeof error === 'string' ? error : error?.message || 'Failed to add domain.';
        notification.error(message);
      }
    },
    [dispatch]
  );

  const handleRemove = useCallback(
    async (id: string | number) => {
      setRemovingId(id);
      try {
        await dispatch(removeDomain({ id })).unwrap();
        notification.success('Domain Removed', 'The domain has been unlinked.');
      } catch (error: any) {
        const message = typeof error === 'string' ? error : error?.message || 'Failed to remove domain.';
        notification.error(message);
      } finally {
        setRemovingId(null);
      }
    },
    [dispatch]
  );

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <GlobalOutlined style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Custom Domains</CardTitleText>
        </CardTitle>
        {!showAddForm && (
          <SaveChangesButton
            icon={<PlusOutlined />}
            onClick={() => setShowAddForm(true)}
            disabled={profileLoading || loading.domains}
          >
            Add Domain
          </SaveChangesButton>
        )}
      </CardHeader>

      {loading.domains ? (
        <DomainSkeletonList>
          {[1, 2].map((i) => (
            <Skeleton.Input key={i} active style={{ width: '100%', height: 60, borderRadius: 8 }} />
          ))}
        </DomainSkeletonList>
      ) : domains.length === 0 && !showAddForm ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <EmptyDescription>
              No domains added yet. Click <strong>Add Domain</strong> to get started.
            </EmptyDescription>
          }
        />
      ) : (
        <div>
          {domains.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              onRemove={handleRemove}
              removing={removingId === d.id}
            />
          ))}
        </div>
      )}

      {showAddForm && (
        <>
          {domains.length > 0 && <Divider style={{ margin: '16px 0' }} />}
          <AddDomainForm
            loading={loading.addDomain}
            onSave={handleAdd}
            onCancel={() => setShowAddForm(false)}
          />
        </>
      )}
    </SettingsCard>
  );
};

export default React.memo(DomainSection);
